import anime from 'animejs';
import { schedule } from '../data/schedule';
import { fitText } from './fit-text';

/**
 * The block over the gameplay capture: the two runner nameplates and their socials, the
 * game tab, the host line and the couch.
 *
 * Note updateCurrentRunInfo() latches on gameTabFilled — the game tab is written exactly
 * once per page load and does not follow a later change of current_timeslot.
 */
export const currentRunRenderer = {
    runnerNameShowDelay: 105000,
    runnerNameShowDuration: 2000,
    runnerSocialShowDelay: 15000,
    gameTabFilled: false,

    /**
     * Which cell of the console sprite each `platform.name` from fungus gets. The sprite
     * only draws a handful of consoles; everything else, PC included, falls back to PC.
     * Spring has a single gamepad image, so the cell only moves the picture in autumn.
     */
    platformIconCells: {
        'PC': 2,
        'PlayStation 2': 3,
        'GameCube': 4,
        'NES': 5, 'Famicom': 5,
        'PlayStation': 6,
        'Nintendo Switch': 7,
        'SNES': 8, 'Super Nintendo': 8, 'Super Famicom': 8,
    },
    platformIconFallbackCell: 2,
    platformIconCell: (platform) =>
        currentRunRenderer.platformIconCells[platform] ?? currentRunRenderer.platformIconFallbackCell,

    /**
     * Consoles too narrow for a square cell come as their own image instead of a sprite
     * cell. The value becomes `data-icon` on `.platform`, which game-tab.scss maps to the file.
     */
    platformIconSingles: {
        'Game Boy': 'gameboy', 'Game Boy Color': 'gameboy', 'Game Boy Advance': 'gameboy',
        'Super Game Boy': 'gameboy', 'Game Boy Player': 'gameboy',
        'Xbox 360': 'xbox360',
        'Wii': 'wii',
    },
    updateRunnerName: () => {
        document.querySelectorAll('.current-runner-1 .runner-name').forEach(el => {
            el.innerHTML = schedule.getRunnerName(1);
        })
        document.querySelectorAll('.current-runner-2 .runner-name').forEach(el => {
            el.innerHTML = schedule.getRunnerName(2);
        })
    },

    fadeInOutRunnerName: (ord) => {
        const runnerName = document.querySelectorAll('.current-runner-' + ord + ' .runner-name');
        if (schedule.getRunnerSocialsData(ord).length === 0) {
            runnerName.forEach(function (e) { e.style.opacity = 1; });
            setTimeout(function () { currentRunRenderer.fadeInOutRunnerName(ord) }, currentRunRenderer.runnerNameShowDelay);
        } else {
            runnerName.forEach(function (e) { e.style.opacity = 0; });
            anime({
                targets: runnerName,
                opacity: 1,
                duration: currentRunRenderer.runnerNameShowDuration,
                endDelay: currentRunRenderer.runnerNameShowDelay / 4,
                easing: 'easeInOutExpo',
                direction: 'alternate',
                complete: function () {
                    currentRunRenderer.fadeInOutRunnerSocials(ord);
                },
            });
        }
    },

    fadeInOutRunnerSocials: (ord) => {
        const socialHandle = document.querySelectorAll('.current-runner-' + ord + ' .social .handle');
        const socialIcon = document.querySelectorAll('.current-runner-' + ord + ' .social .icon');
        let socials = schedule.getRunnerSocialsData(ord);
        let handleTimeline = anime.timeline();
        let iconTimeline = anime.timeline();
        socials.forEach(function callback(socialData, i) {
            handleTimeline.add({
                targets: socialHandle,
                opacity: 1,
                duration: currentRunRenderer.runnerNameShowDuration,
                endDelay: currentRunRenderer.runnerSocialShowDelay,
                easing: 'easeInOutExpo',
                begin: function () {
                    socialHandle.forEach(function (el) { el.innerHTML = socialData.handle; });
                },
            }).add({
                targets: socialHandle,
                opacity: 0,
                duration: currentRunRenderer.runnerNameShowDuration,
                easing: 'easeInOutExpo',
                complete: function () {
                    if (i === socials.length - 1) {
                        currentRunRenderer.fadeInOutRunnerName(ord);
                    }
                }
            });
            iconTimeline.add({
                targets: socialIcon,
                translateX: {
                    value: 43,
                    duration: 200,
                },
                opacity: [
                    {
                        value: 0,
                        duration: 0,
                    },
                    {
                        value: 1,
                        duration: 200,
                    },
                ],
                endDelay: currentRunRenderer.runnerSocialShowDelay + (1800 * 2),
                easing: 'easeInOutExpo',
                begin: function () {
                    socialIcon.forEach(function (el) { el.src = socialData.icon; });
                },
            }).add({
                targets: socialIcon,
                translateX: {
                    value: 0,
                    duration: 200,
                },
                opacity: {
                    value: 0,
                    duration: 200,
                },
                easing: 'easeInOutExpo',
            });
        });
    },

    updateCurrentRunInfo: () => {
        if (currentRunRenderer.gameTabFilled) return;

        const titleElement = document.querySelector('#current-run-game-tab .title');
        const categoryElement = document.querySelector('#current-run-game-tab .category');
        const elapsedElement = document.querySelector('#current-run-game-tab .elapsed');
        const platformElement = document.querySelector('#current-run-game-tab .platform');
        const yearElement = document.querySelector('#current-run-game-tab .year');

        let playingNow = schedule.getPlayingNow();

        const gameName = playingNow?.name ?? '';
        const categoryName = playingNow?.submission?.category ?? '';
        const platform = playingNow?.submission?.platform?.name ?? '';
        titleElement.innerHTML = gameName;
        categoryElement.innerHTML = categoryName;
        platformElement.innerHTML = platform;
        platformElement.style.setProperty('--platform-cell', currentRunRenderer.platformIconCell(platform));
        const single = currentRunRenderer.platformIconSingles[platform];
        if (single) platformElement.dataset.icon = single;
        else delete platformElement.dataset.icon;

        if (!!gameName) titleElement.classList.add('show');
        if (!!categoryName) categoryElement.classList.add('show');
        if (!!platform) platformElement.classList.add('show');

        let estimateDuration = playingNow?.duration ?? 0;
        let estimate = schedule.getDurationFromSeconds(estimateDuration);

        elapsedElement.innerHTML = estimate.format('HH:mm:ss');
        if (estimateDuration !== 0)
            elapsedElement.classList.add('show');

        const year = playingNow?.submission?.year;
        yearElement.innerHTML = year;
        if (!!year)
            yearElement.classList.add('show');

        if (!!gameName)
            currentRunRenderer.gameTabFilled = true;

        const currentRunElement = document.querySelector('.info-bg');
        currentRunElement.classList.remove('r16-9');
        currentRunElement.classList.remove('r4-3');
        const runRatio = playingNow?.ratio_override ?? playingNow?.submission?.aspect_ratio ?? '16:9';
        let ratio = '';
        if (runRatio === '4:3') {
            ratio = 'r4-3';
        } else {
            ratio = 'r16-9';
        }

        console.log("Ratio:", ratio);
        currentRunElement.classList.add(ratio);

        fitText.decreaseFontSizeUntilItFits(titleElement);
    },

    updateHostName: () => {
        currentRunRenderer.setHostName(schedule.getHostName());
    },

    setHostName: (hostName) => {
        const hostNameElement = document.querySelector('#host .host-name');
        hostNameElement.innerHTML = hostName;
    },

    updateCouch: () => {
        const couchElement = document.querySelector('#couch');
        const couchNamesElement = couchElement.querySelector('.couch-names');

        const couchNames = schedule.getCouch();
        couchNamesElement.innerHTML = couchNames;

        if (!couchNames)
            couchElement.style.opacity = 0;
        else
            couchElement.style.opacity = 1;
    },

    updateBreakScreenHost: () => {
        const breakHostElement = document.querySelector('#break-host');
        const breakHostNameElement = breakHostElement.querySelector('.break-host-name');

        const breakHostName = schedule.getBreakScreenHost();
        breakHostNameElement.innerHTML = breakHostName;

        if (!breakHostName)
            breakHostElement.style.opacity = 0;
        else
            breakHostElement.style.opacity = 1;
    },

};

window.currentRunRenderer = currentRunRenderer;
